import { useState, useEffect } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import { DEFAULT_NEWS, type NewsItem } from '../data/news';
import AdminLayout from '../components/AdminLayout';
import { Save, CheckCircle2, Trash2, Plus, Newspaper } from 'lucide-react';

/* Admin editor for the client News / Advisory feed — Firestore `news/current`.
   Drives the News section on the client dashboard (useNews). */

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:border-[var(--brand)]';
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';
const blank = (): NewsItem => ({ id: 'n' + Date.now(), title: '', category: 'Update', url: '', image: '', author: 'Conncct Advisory', readTime: '3 min read', published: true });

export default function AdminNews() {
  const [items, setItems] = useState<NewsItem[]>(DEFAULT_NEWS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'news', 'current'));
        if (snap.exists() && Array.isArray(snap.data().items)) setItems(snap.data().items);
      } catch (e) { console.error('Failed to load news/current:', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const patch = (i: number, fn: (n: NewsItem) => void) => {
    setItems(prev => { const next = prev.map(x => ({ ...x })); const item = next[i]; if (item) fn(item); return next; });
    setSavedAt(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('news/current', { items });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'news/current');
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.code === 'permission-denied' ? 'Save failed. Permission denied. Sign in as a Firebase-authenticated admin.' : 'Save failed. Check console.');
    } finally { setSaving(false); }
  };

  const LAYOUT = {
    section: 'Content',
    title: 'News & Advisory',
    icon: Newspaper,
    subtitle: 'Curates the News section on every client dashboard, live instantly. Placeholder content shows until you publish your own.',
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading news…" />;

  return (
    <AdminLayout {...LAYOUT} spaced>
      <button onClick={() => { setItems(prev => [blank(), ...prev]); setSavedAt(null); }} className="flex items-center gap-2 border border-gray-200 bg-white text-gray-700 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50">
        <Plus className="w-4 h-4" /> Add article
      </button>

      {items.length === 0 && <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center text-gray-400 font-medium">No articles yet. Add one above.</div>}

      {items.map((n, i) => (
        <div key={n.id || i} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Article {i + 1}</span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600 cursor-pointer select-none">
                <input type="checkbox" checked={n.published} onChange={e => patch(i, x => { x.published = e.target.checked; })} className="w-4 h-4 rounded border-gray-300 accent-[var(--brand)]" /> Published
              </label>
              <button onClick={() => { setItems(prev => prev.filter((_, j) => j !== i)); setSavedAt(null); }} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className={lbl}>Title</label><input className={inputCls} value={n.title} onChange={e => patch(i, x => { x.title = e.target.value; })} placeholder="Article headline" /></div>
            <div><label className={lbl}>Category</label><input className={inputCls} value={n.category} onChange={e => patch(i, x => { x.category = e.target.value; })} placeholder="e.g. Tax & Compliance" /></div>
            <div><label className={lbl}>Read time</label><input className={inputCls} value={n.readTime} onChange={e => patch(i, x => { x.readTime = e.target.value; })} placeholder="5 min read" /></div>
            <div className="sm:col-span-2"><label className={lbl}>Article link (URL)</label><input className={inputCls} value={n.url} onChange={e => patch(i, x => { x.url = e.target.value; })} placeholder="https://…" /></div>
            <div className="sm:col-span-2"><label className={lbl}>Cover image URL</label><input className={inputCls} value={n.image} onChange={e => patch(i, x => { x.image = e.target.value; })} placeholder="https://… (optional)" /></div>
            <div><label className={lbl}>Author</label><input className={inputCls} value={n.author} onChange={e => patch(i, x => { x.author = e.target.value; })} /></div>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3 flex-wrap pb-8 pt-2">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[var(--brand)] hover:bg-[var(--brand-h)] disabled:opacity-60 text-[var(--brand-on)] font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5">
          <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save news'}
        </button>
        {savedAt && <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Saved · live as of {savedAt}</span>}
      </div>
    </AdminLayout>
  );
}
